/** Query param set when the browser extension opened the web app tab. */
export const EXTENSION_OPENED_QUERY_PARAM = "ext";
export const EXTENSION_OPENED_QUERY_VALUE = "1";
export const EXTENSION_OPENED_SESSION_STORAGE_KEY = "starmesh_ext_opened";

/** Must match the extension page-to-content-script bridge constants. */
export const WEBAPP_CLOSE_EXTENSION_TAB_MESSAGE =
  "STARMESH_CLOSE_EXTENSION_WEBAPP_TAB";
export const WEBAPP_CLOSE_EXTENSION_TAB_SOURCE = "starmesh-webapp";

/**
 * Test whether a URL search or path contains the extension-opened marker.
 *
 * @param {string} search Query string such as `"?ext=1"`.
 * @param {string} path Optional relative or absolute URL.
 * @returns {boolean} Whether either input contains `ext=1`.
 * @example
 * isExtensionOpenedTab("?ext=1");
 * // true
 */
export const isExtensionOpenedTab = (search = "", path = "") => {
  const parseSearch = (raw) =>
    new URLSearchParams(String(raw || "").replace(/^\?/, "")).get(
      EXTENSION_OPENED_QUERY_PARAM
    ) === EXTENSION_OPENED_QUERY_VALUE;

  if (parseSearch(search)) return true;
  const pathString = String(path || "").trim();
  if (!pathString) return false;
  try {
    return parseSearch(new URL(pathString, "https://local.invalid").search);
  } catch (_) {
    return false;
  }
};

/**
 * Persist the extension marker across sign-in and integration OAuth redirects.
 *
 * @returns {void}
 * @example
 * persistExtensionOpenedTab();
 * // sessionStorage.starmesh_ext_opened is "1" when the URL contains ?ext=1
 */
export const persistExtensionOpenedTab = () => {
  if (typeof window === "undefined") return;
  const redirectPath = window.sessionStorage.getItem("redirectPath");
  if (
    isExtensionOpenedTab(window.location.search) ||
    isExtensionOpenedTab("", redirectPath)
  ) {
    window.sessionStorage.setItem(
      EXTENSION_OPENED_SESSION_STORAGE_KEY,
      EXTENSION_OPENED_QUERY_VALUE
    );
  }
};

/**
 * Return whether the current tab was opened by the browser extension.
 *
 * @returns {boolean} True for a current or persisted `ext=1` marker.
 * @example
 * isExtensionOpenedTabPersisted();
 * // true after persistExtensionOpenedTab() on a URL containing ?ext=1
 */
export const isExtensionOpenedTabPersisted = () => {
  if (typeof window === "undefined") return false;
  return (
    isExtensionOpenedTab(window.location.search) ||
    window.sessionStorage.getItem(EXTENSION_OPENED_SESSION_STORAGE_KEY) ===
      EXTENSION_OPENED_QUERY_VALUE
  );
};

/**
 * Return whether sign-in should close this extension-opened tab.
 *
 * @returns {boolean} True when the extension marker is persisted.
 * @example
 * shouldCloseExtensionOpenedTabAfterLogin();
 * // true when sessionStorage.starmesh_ext_opened is "1"
 */
export const shouldCloseExtensionOpenedTabAfterLogin = () =>
  isExtensionOpenedTabPersisted();

/**
 * Ask the extension to close its web-app tab, then call `window.close()`.
 *
 * @param {string} workflowId Optional workflow selected before OAuth.
 * @returns {boolean} True when a close was attempted.
 * @example
 * tryCloseExtensionOpenedTab("workflow-123");
 * // true when this tab was opened with ?ext=1
 */
export const tryCloseExtensionOpenedTab = (workflowId = "") => {
  if (typeof window === "undefined" || !isExtensionOpenedTabPersisted()) {
    return false;
  }
  window.sessionStorage.removeItem(EXTENSION_OPENED_SESSION_STORAGE_KEY);
  try {
    window.postMessage(
      {
        type: WEBAPP_CLOSE_EXTENSION_TAB_MESSAGE,
        source: WEBAPP_CLOSE_EXTENSION_TAB_SOURCE,
        workflowId: String(workflowId || "").trim(),
      },
      window.location.origin
    );
  } catch (_) {
    // Fall through to window.close().
  }
  window.close();
  return true;
};
